import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  KibMultiSelection,
  type KibMultiSelectionOption,
} from '@chewy/kib-fields-new-react'
import { useDebounce } from 'use-debounce'
import { Defaults } from '@pbt/pbt-ui-components'

import {
  useGetRoleIdsList,
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import SecretSearchContext from '~/constants/secretSearchContext'
import {
  clearDynamicSearchCache,
  fetchSuggestionResults,
} from '~/store/actions/search'
import { getStateSpecificSuggestionResults } from '~/store/reducers/search'
import { getUsersMap } from '~/store/reducers/users'
import { type SuggestionResult, TeamFilter } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import '@chewy/kib-fields-new-styles/dist/styles/kib-multi-selection.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'

export interface KibUserSelectProps {
  disabled?: boolean
  filterScope?: UserSelectFilterScope
  includeInactive?: boolean
  isLoading?: boolean
  label?: string
  onChange?: (e: string[]) => void
  placeHolder: string
  readOnly?: boolean
  required?: boolean
  teamFilter?: TeamFilter
  useInMemory?: boolean
  userMultiSelectStateId: string
  value?: string[]
}

export const KibUserMultiSelect = ({
  value,
  onChange,
  label,
  includeInactive = false,
  teamFilter = TeamFilter.MEMBER,
  placeHolder,
  disabled,
  required = false,
  readOnly,
  filterScope = UserSelectFilterScope.MainStaff,
  userMultiSelectStateId,
  isLoading: isLoadingProp,
  useInMemory,
}: KibUserSelectProps) => {
  const dispatch = useDispatch()
  const roleIdsList = useGetRoleIdsList(filterScope)
  const searchResult = useSelector(
    getStateSpecificSuggestionResults(userMultiSelectStateId),
  )
  const isLoading = searchResult.isSuggestionReceiving
  const searchResults: SuggestionResult[] =
    searchResult.suggestionResults as SuggestionResult[]
  const usersMap = useSelector(getUsersMap)
  const maxCount = useInMemory ? 50 : undefined
  const [userOptions, setUserOptions] = useState<KibMultiSelectionOption[]>([])
  const [internalValue, setInternalValue] = useState<KibMultiSelectionOption[]>(
    [],
  )
  const [rawInputValue, setRawInputValue] = useState('')
  const [inputValue] = useDebounce(rawInputValue, Defaults.DEBOUNCE_ACTION_TIME)
  const userIds = value

  const dispatchFetchSearch = (searchTerm: string) => {
    dispatch(
      fetchSuggestionResults({
        searchContext: SecretSearchContext.PERSONS,
        searchTerm,
        teamFilter,
        roleIds: roleIdsList.join(','),
        includeInactive,
        maxCount,
        shorten: true,
        stateId: userMultiSelectStateId,
      }),
    )
  }

  useEffect(
    () => () => {
      dispatch(clearDynamicSearchCache(userMultiSelectStateId))
    },
    [],
  )

  useEffect(() => {
    if (userIds && !inputValue) {
      const userOptionsFromField = userIds
        .map((userId) => {
          if (usersMap[userId]) {
            return {
              label: `${usersMap[userId]?.firstName || ''} ${
                usersMap[userId]?.lastName || ''
              }`,
              value: userId,
            }
          }
          return null
        })
        .filter(Boolean) as KibMultiSelectionOption[]
      setInternalValue(userOptionsFromField)
      setUserOptions(userOptionsFromField)
    }
  }, [userIds])

  useEffect(() => {
    if (searchResults && inputValue) {
      const userOptionsResult = searchResults.map((result) => ({
        label: result.item ?? '',
        value: result.id,
      }))
      setUserOptions(userOptionsResult)
    }
  }, [searchResults, inputValue])

  useEffectExceptOnMount(() => {
    if (inputValue) {
      dispatchFetchSearch(inputValue)
    }
  }, [inputValue])

  useEffectExceptOnMount(() => {
    if (onChange) {
      const internalUserIds = internalValue
        .map((o) => usersMap[o.value])
        .filter(Boolean)
        .map((u) => u.id)
      onChange(internalUserIds)
    }
  }, [JSON.stringify(internalValue)])

  const onQuery = (
    v?: string,
    e?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e?.type === 'change' && v && v.length) {
      setRawInputValue(v)
    }
  }

  return (
    <KibMultiSelection
      isAsync
      disabled={disabled || readOnly}
      label={label || ''}
      loading={isLoading || isLoadingProp}
      options={userOptions}
      placeholder={placeHolder}
      required={required}
      value={internalValue}
      onChange={(v) => setInternalValue(v)}
      onQuery={onQuery}
    />
  )
}
